import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import Modal from "../../../../../components/Modal/Modal";
import Button from "../../../../../components/Button/Button";
import DragIndicatorIcon from "../../../../../assets/svg/drag-indicator-icon";
import CheckboxCheckedIcon from "../../../../../assets/svg/checkbox-checked-icon";
import CloseIcon from "../../../../../assets/svg/close-icon";
import ChevronDownIcon from "../../../../../assets/svg/chevron-down-icon";
import type { LeadColumnConfig } from "../utlis/leadTableColumns";
import ApplyFiltersIcon from "../../../../../assets/svg/apply-filters-icon";

const PIN_COUNT_OPTIONS = [0, 1, 2, 3, 4];

interface EditColumnsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: LeadColumnConfig[];
  onApply: (columns: LeadColumnConfig[]) => void;
  pinnedCount: number;
  onPinnedCountChange: (count: number) => void;
}

const EditColumnsModal: React.FC<EditColumnsModalProps> = ({
  open,
  onOpenChange,
  columns,
  onApply,
  pinnedCount,
  onPinnedCountChange,
}) => {
  const [localColumns, setLocalColumns] = useState<LeadColumnConfig[]>([]);
  const [localPinnedCount, setLocalPinnedCount] = useState(pinnedCount);
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [isPinDropdownOpen, setIsPinDropdownOpen] = useState(false);
  const pinDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setLocalColumns(columns.map((c) => ({ ...c })));
      setLocalPinnedCount(pinnedCount);
    }
  }, [open, columns, pinnedCount]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pinDropdownRef.current &&
        !pinDropdownRef.current.contains(e.target as Node)
      ) {
        setIsPinDropdownOpen(false);
      }
    };
    if (isPinDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPinDropdownOpen]);

  const visibleColumns = useMemo(
    () => localColumns.filter((c) => c.visible),
    [localColumns]
  );

  const hasChanges = useMemo(() => {
    if (localPinnedCount !== pinnedCount) return true;
    return localColumns.some(
      (col, i) =>
        col.visible !== columns[i]?.visible || col.key !== columns[i]?.key
    );
  }, [localColumns, columns, localPinnedCount, pinnedCount]);

  const handleToggle = useCallback((key: string) => {
    setLocalColumns((prev) =>
      prev.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c))
    );
  }, []);

  const handleRemove = useCallback((key: string) => {
    setLocalColumns((prev) =>
      prev.map((c) => (c.key === key ? { ...c, visible: false } : c))
    );
  }, []);

  const handleRemoveAll = useCallback(() => {
    setLocalColumns((prev) => prev.map((c) => ({ ...c, visible: false })));
    setLocalPinnedCount(0);
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, key: string) => {
    e.dataTransfer.setData("text/plain", key);
    setDraggedKey(key);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, key: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (key !== draggedKey) setDragOverKey(key);
    },
    [draggedKey]
  );

  const handleDragLeave = useCallback(() => {
    setDragOverKey(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedKey(null);
    setDragOverKey(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetKey: string) => {
      e.preventDefault();
      const sourceKey = draggedKey ?? e.dataTransfer.getData("text/plain");
      if (!sourceKey || sourceKey === targetKey) {
        setDraggedKey(null);
        setDragOverKey(null);
        return;
      }

      setLocalColumns((prev) => {
        const visible = prev.filter((c) => c.visible);
        const hidden = prev.filter((c) => !c.visible);

        const sourceIdx = visible.findIndex((c) => c.key === sourceKey);
        const targetIdx = visible.findIndex((c) => c.key === targetKey);
        if (sourceIdx === -1 || targetIdx === -1) return prev;

        const reordered = [...visible];
        const [removed] = reordered.splice(sourceIdx, 1);
        reordered.splice(targetIdx, 0, removed);

        return [...reordered, ...hidden];
      });

      setDraggedKey(null);
      setDragOverKey(null);
    },
    [draggedKey]
  );

  const handleApply = useCallback(() => {
    onApply(localColumns);
    onPinnedCountChange(localPinnedCount);
    onOpenChange(false);
  }, [localColumns, localPinnedCount, onApply, onPinnedCountChange, onOpenChange]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const customHeader = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <div>
          <ApplyFiltersIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
        </div>
        <h1 className="text-lg font-medium text-verba-indigo dark:text-verba-indigo-dk">
          Choose which columns you see
        </h1>
      </div>
      <button
        className="w-[15px] h-[15px] ml-auto self-start bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110"
        onClick={() => onOpenChange(false)}
      >
        <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
      </button>
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title=""
      showHeader
      showCloseButton={false}
      customHeader={customHeader}
      headerClassName="px-5 py-4"
      bodyClassName="!p-0 flex flex-col"
      contentClassName="w-[640px] max-w-[640px] bg-pure-white dark:bg-pure-white-dk rounded-[16px]"
      disableDefaultAnimation
      contentStyle={{
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      <div className="flex flex-1 min-h-0 px-5 gap-5">
        {/* Left panel */}
        <div className="w-[180px] shrink-0 flex flex-col">
          <h3 className="text-md font-medium text-dark-gray dark:text-dark-gray-dk mb-3">
            All Columns
          </h3>
          <div className="flex flex-col gap-0.5 max-h-[360px] overflow-y-auto">
            {localColumns.map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => handleToggle(col.key)}
                className="flex items-center gap-2.5 py-1.5 text-left rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                aria-checked={col.visible}
                role="checkbox"
              >
                {col.visible ? (
                  <CheckboxCheckedIcon className="w-3.5 h-3.5 text-verba-blue dark:text-verba-blue-dk shrink-0" />
                ) : (
                  <span className="w-3.5 h-3.5 rounded border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk shrink-0" />
                )}
                <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                  {col.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-md font-medium text-dark-gray dark:text-dark-gray-dk">
              Arrange Columns ({visibleColumns.length})
            </h3>
            <div className="relative flex items-center gap-2" ref={pinDropdownRef}>
              <span className="text-dark-gray dark:text-dark-gray-dk text-sm">
                Pinned Columns
              </span>
              <button
                type="button"
                onClick={() => setIsPinDropdownOpen((p) => !p)}
                className="flex w-[56px] items-center gap-3 text-md font-medium text-verba-indigo dark:text-verba-indigo-dk border border-border-gray dark:border-border-gray-dk rounded-md px-2 py-0.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              >
                <span className="text-verba-indigo dark:text-verba-indigo-dk font-semibold text-sm min-w-[14px] text-center">
                  {localPinnedCount}
                </span>
                <ChevronDownIcon
                  className={`w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk transition-transform ${isPinDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isPinDropdownOpen && (
                <div className="absolute top-[30px] right-0 z-50 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-md shadow-popper-blur-effect overflow-hidden w-[56px]">
                  {PIN_COUNT_OPTIONS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        setLocalPinnedCount(n);
                        setIsPinDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-1.5 text-sm text-center hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors ${
                        localPinnedCount === n
                          ? "bg-card-highlight dark:bg-card-highlight-dk font-semibold text-verba-blue dark:text-verba-blue-dk"
                          : "text-verba-indigo dark:text-verba-indigo-dk"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 max-h-[360px] overflow-y-auto">
            {visibleColumns.map((col, idx) => {
              const isDragging = draggedKey === col.key;
              const isDragOver =
                dragOverKey === col.key && draggedKey !== col.key;
              const showFrozenSeparator =
                localPinnedCount > 0 && idx === localPinnedCount - 1;

              return (
                <React.Fragment key={col.key}>
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, col.key)}
                    onDragOver={(e) => handleDragOver(e, col.key)}
                    onDrop={(e) => handleDrop(e, col.key)}
                    onDragEnd={handleDragEnd}
                    onDragLeave={handleDragLeave}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border bg-pure-white dark:bg-pure-white-dk cursor-grab active:cursor-grabbing transition-all ${
                      isDragOver
                        ? "border-verba-blue dark:border-verba-blue-dk"
                        : "border-border-gray dark:border-border-gray-dk"
                    } ${isDragging ? "opacity-70" : ""}`}
                  >
                    <div
                      className="shrink-0 touch-none select-none"
                      role="button"
                      tabIndex={0}
                      aria-label="Drag to reorder"
                    >
                      <DragIndicatorIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
                    </div>
                    <span className="flex-1 font-medium text-md text-heading-text dark:text-heading-text-dk truncate">
                      {col.label}
                    </span>
                    <button
                      className="w-[12px] h-[12px] ml-auto self-center bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(col.key);
                      }}
                    >
                      <CloseIcon className="w-[4px] h-[4px] text-verba-indigo dark:text-verba-indigo-dk" />
                    </button>
                  </div>
                  {showFrozenSeparator && (
                    <div className="flex items-center gap-2 py-1">
                      <div className="flex-1 border-t border-[0.5px] border-border-gray dark:border-border-gray-dk" />
                      <span className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk whitespace-nowrap">
                        Above column(s) are frozen
                      </span>
                      <div className="flex-1 border-t border-[0.5px] border-border-gray dark:border-border-gray-dk" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            {visibleColumns.length === 0 && (
              <p className="text-md text-gray-placeholder dark:text-gray-placeholder-dk py-4 text-center">
                No columns selected
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-4 mt-4 border-t border-border-gray dark:border-border-gray-dk">
        <button
          type="button"
          onClick={handleRemoveAll}
          className="text-md font-medium text-verba-red-alert dark:text-verba-red-alert-dk border border-verba-red-alert dark:border-verba-red-alert-dk rounded-lg px-4 py-1.5 hover:opacity-80"
        >
          Remove all columns
        </button>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            innerText="Cancel"
            onClick={handleCancel}
            buttonContainerClass="px-5 py-1.5 rounded-lg border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            innerTextClass="text-md font-medium text-heading-text dark:text-heading-text-dk"
          />
          <Button
            type="button"
            innerText="Apply"
            onClick={handleApply}
            disabled={!hasChanges}
            buttonContainerClass={`px-6 py-1.5 rounded-lg ${
              hasChanges
                ? "bg-verba-indigo dark:bg-verba-indigo-dk"
                : "bg-gray-placeholder dark:bg-gray-placeholder-dk cursor-not-allowed"
            }`}
            innerTextClass="text-md font-medium text-pure-white dark:text-pure-white-dk"
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditColumnsModal;
